/**
 * Next.js Logger Utility - Istanbul Timezone Support
 *
 * Intercepts Next.js console logs and formats them with Istanbul timezone (+03:00)
 * Works with PM2 log format: YYYY-MM-DD HH:mm:ss Z
 *
 * Created: 2025-12-12
 */

import { formatInTimeZone } from "date-fns-tz";

const ISTANBUL_TZ = "Europe/Istanbul";
const LOG_FORMAT = "yyyy-MM-dd HH:mm:ss";

/**
 * Format timestamp in Istanbul timezone with +03:00 offset
 */
function formatIstanbulTimestamp(): string {
  const now = new Date();
  const istanbulTime = formatInTimeZone(now, ISTANBUL_TZ, LOG_FORMAT);
  const offset = "+03:00"; // Istanbul is UTC+3
  return `${istanbulTime} ${offset}`;
}

/**
 * Override console methods to include Istanbul timezone timestamps
 * This works for Next.js server-side logs
 */
export function initNextLogger() {
  if (typeof window !== "undefined") {
    // Client-side: Use browser's native console (PM2 handles server-side)
    return;
  }

  // Server-side: Override console methods
  // eslint-disable-next-line no-console
  const originalLog = console.log.bind(console);
  // eslint-disable-next-line no-console
  const originalError = console.error.bind(console);
  // eslint-disable-next-line no-console
  const originalWarn = console.warn.bind(console);
  // eslint-disable-next-line no-console
  const originalInfo = console.info.bind(console);
  // eslint-disable-next-line no-console
  const originalDebug = console.debug.bind(console);

  // Format: [timestamp] [level] message
  const createLogPrefix = (level: string) => {
    return `[${formatIstanbulTimestamp()}] [${level.toUpperCase()}]`;
  };

  // eslint-disable-next-line no-console
  console.log = (...args: any[]) => {
    originalLog(createLogPrefix("log"), ...args);
  };

  // eslint-disable-next-line no-console
  console.error = (...args: any[]) => {
    originalError(createLogPrefix("error"), ...args);
  };

  // eslint-disable-next-line no-console
  console.warn = (...args: any[]) => {
    originalWarn(createLogPrefix("warn"), ...args);
  };

  // eslint-disable-next-line no-console
  console.info = (...args: any[]) => {
    originalInfo(createLogPrefix("info"), ...args);
  };

  // eslint-disable-next-line no-console
  console.debug = (...args: any[]) => {
    originalDebug(createLogPrefix("debug"), ...args);
  };
}

/**
 * Format Next.js log message with Istanbul timezone
 * Used for custom log messages
 */
export function formatNextLog(
  level: "log" | "error" | "warn" | "info" | "debug",
  ...args: any[]
): string {
  const timestamp = formatIstanbulTimestamp();
  const levelUpper = level.toUpperCase().padEnd(5);
  const message = args
    .map((arg) => (typeof arg === "object" ? JSON.stringify(arg) : String(arg)))
    .join(" ");

  return `[${timestamp}] [${levelUpper}] ${message}`;
}

/**
 * Next.js custom logger instance
 */
export const nextLogger = {
  log: (...args: any[]) => {
    if (typeof window !== "undefined") {
      // eslint-disable-next-line no-console
      console.log(...args);
    } else {
      // eslint-disable-next-line no-console
      console.log(formatNextLog("log", ...args));
    }
  },
  error: (...args: any[]) => {
    if (typeof window !== "undefined") {
      // eslint-disable-next-line no-console
      console.error(...args);
    } else {
      // eslint-disable-next-line no-console
      console.error(formatNextLog("error", ...args));
    }
  },
  warn: (...args: any[]) => {
    if (typeof window !== "undefined") {
      // eslint-disable-next-line no-console
      console.warn(...args);
    } else {
      // eslint-disable-next-line no-console
      console.warn(formatNextLog("warn", ...args));
    }
  },
  info: (...args: any[]) => {
    if (typeof window !== "undefined") {
      // eslint-disable-next-line no-console
      console.info(...args);
    } else {
      // eslint-disable-next-line no-console
      console.info(formatNextLog("info", ...args));
    }
  },
  debug: (...args: any[]) => {
    if (typeof window !== "undefined") {
      // eslint-disable-next-line no-console
      console.debug(...args);
    } else {
      // eslint-disable-next-line no-console
      console.debug(formatNextLog("debug", ...args));
    }
  },
};
